import { useEffect, useRef, useState } from 'react';
import { Button } from '@/ui/components/ui/button';
import { Input } from '@/ui/components/ui/input';
import { Spinner } from '@/ui/components/ui/spinner';
import { Textarea } from '@/ui/components/ui/textarea';
import { cn } from '@/ui/lib/utils';

const TITLE_MAX = 80;
const DESC_MAX = 200;

function CharCount({ current, max }: { current: number; max: number }) {
	const ratio = current / max;
	const color =
		ratio >= 1
			? 'text-destructive'
			: ratio >= 0.8
				? 'text-orange-500'
				: 'text-muted-foreground';
	return (
		<span
			className={cn(
				'text-xs font-medium tabular-nums transition-colors',
				color,
			)}
		>
			{current}/{max}
		</span>
	);
}

interface FeatureFormProps {
	isOpen: boolean;
	submitting?: boolean;
	error?: string | null;
	onSubmit: (title: string, description: string) => void;
	onClose: () => void;
}

export function FeatureForm({
	isOpen,
	submitting = false,
	error,
	onSubmit,
	onClose,
}: FeatureFormProps) {
	const [title, setTitle] = useState('');
	const [desc, setDesc] = useState('');
	const [focused, setFocused] = useState<'title' | 'desc' | null>(null);
	const titleRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (isOpen) {
			const t = setTimeout(() => titleRef.current?.focus(), 350);
			return () => clearTimeout(t);
		}
	}, [isOpen]);

	const canSubmit =
		!submitting &&
		title.trim().length > 0 &&
		title.length <= TITLE_MAX &&
		desc.length <= DESC_MAX;

	const handleSubmit = () => {
		if (!canSubmit) return;
		onSubmit(title.trim(), desc.trim());
	};

	const inputCn = (name: 'title' | 'desc') =>
		cn(
			'h-auto py-3 px-4 bg-background text-foreground focus-visible:ring-0',
			focused === name
				? 'border-ring shadow-[0_0_0_3px_var(--secondary)]'
				: 'border-transparent',
		);

	return (
		<div
			style={{
				transition:
					'max-height 0.4s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.3s ease, margin-bottom 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
				maxHeight: isOpen ? '380px' : '0',
				opacity: isOpen ? 1 : 0,
				marginBottom: isOpen ? '16px' : '0',
				clipPath: isOpen
					? 'inset(-16px -16px -16px -16px)'
					: 'inset(0 -16px 100% -16px)',
				overflow: isOpen ? 'visible' : 'hidden',
			}}
		>
			<div className='shadow-ionos-card bg-card rounded-2xl p-4'>
				<div className='flex justify-end mb-1'>
					<CharCount current={title.length} max={TITLE_MAX} />
				</div>
				<Input
					ref={titleRef}
					value={title}
					disabled={submitting}
					onChange={(e) => {
						if (e.target.value.length <= TITLE_MAX)
							setTitle(e.target.value);
					}}
					onFocus={() => setFocused('title')}
					onBlur={() => setFocused(null)}
					placeholder="I'd like to see…"
					onKeyDown={(e) => {
						if (e.key === 'Enter') handleSubmit();
						if (e.key === 'Escape') onClose();
					}}
					className={cn(inputCn('title'), 'mb-2 font-medium')}
				/>
				<div className='flex justify-end mb-1'>
					<CharCount current={desc.length} max={DESC_MAX} />
				</div>
				<Textarea
					value={desc}
					disabled={submitting}
					onChange={(e) => {
						if (e.target.value.length <= DESC_MAX)
							setDesc(e.target.value);
					}}
					onFocus={() => setFocused('desc')}
					onBlur={() => setFocused(null)}
					placeholder='Add some detail (optional)'
					rows={2}
					onKeyDown={(e) => {
						if (e.key === 'Escape') onClose();
					}}
					className={cn(inputCn('desc'), 'mb-3 resize-y')}
				/>
				{error && (
					<div className='mb-2 px-3 py-2 text-[13px] text-destructive bg-destructive/10 rounded-lg leading-snug animate-shake'>
						{error}
					</div>
				)}
				<div className='flex justify-end'>
					<Button
						onClick={handleSubmit}
						disabled={!canSubmit}
						className={cn(
							'h-auto px-5 py-2.5 rounded-lg tracking-[0.01em] disabled:opacity-100 gap-2',
							canSubmit
								? 'bg-primary text-primary-foreground hover:bg-primary/85 cursor-pointer'
								: 'bg-muted text-muted-foreground cursor-default',
						)}
					>
						{submitting && (
							<Spinner
								size='sm'
								className='text-primary-foreground'
							/>
						)}
						{submitting ? 'Submitting…' : 'Submit feature'}
					</Button>
				</div>
			</div>
		</div>
	);
}
